import { Alert, Box, Cards, Header } from '@amzn/awsui-components-core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Application, LoadingStatus } from 'src/components/context/AppContextModels';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { ErrorFallback } from 'src/components/generic-components/ErrorFallback';
import { logger } from 'src/logger';
import { EmbedAppErrorState } from './EmbedAppErrorState';
import { EmbedAppLoadingState } from './EmbedAppLoadingState';
import IFrameComponent from './IFrameComponent';
import QuickSightDashboardComponent from './QuickSightDashboardComponent';
import TableauVizComponent from './TableauVizComponent';
import { EmbeddableProductType } from './types/EmbeddingInterface';
import * as EmbeddingUtils from './utils/EmbeddingUtils';
import { FinsuiteIssueActionButton } from 'src/components/generic-components/action-buttons/AlertActionTicketButton';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';

interface EmbedAppProps {
  application: Application;
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

type EmbeddingStatus = 'in-progress' | 'success' | 'error';

/**
 * Component for embedding DaS FinSuite applications within an iframe.
 *
 * @param props - Component props containing application data and event handlers
 * @returns JSX element for the embedded application or appropriate state component
 */
export const EmbedApp: React.FC<EmbedAppProps> = ({ application, onLoad, onError }) => {
  // All hooks must be called before any conditional return (React Rules of Hooks)
  const { Alias: userAlias } = useAuth();
  const { contextLoadingStatus } = useAppContext();
  const { isDarkMode } = useTheme();
  // Capture the initial theme for the iframe URL — subsequent theme changes
  // are handled via postMessage (THEME_CHANGE) without reloading the iframe.
  const initialIsDarkMode = useRef(isDarkMode).current;

  const [embeddingUrl, setEmbeddingUrl] = useState<string | null>(null);
  const [readyToEmbed, setReadyToEmbed] = useState<EmbeddingStatus>('in-progress');
  const [embeddingUrlError, setEmbeddingUrlError] = useState<string | null>(null);

  const productType = application ? EmbeddingUtils.getProductType(application) : null;

  // Non-prod environment selection
  const isNonProd = ENVIRONMENT_VARIABLES.env.Stage !== 'prod';
  const nonProdEnvs = application ? EmbeddingUtils.getNonProdEnvironments(application) : [];
  const showEnvPicker = isNonProd && nonProdEnvs.length > 1;
  const [selectedEnvLink, setSelectedEnvLink] = useState<string | null>(
    !showEnvPicker && nonProdEnvs.length === 1 ? nonProdEnvs[0].link || null : null
  );

  // Reset environment selection when application changes
  useEffect(() => {
    const autoSelect = !isNonProd || nonProdEnvs.length <= 1;
    setSelectedEnvLink(autoSelect && nonProdEnvs.length === 1 ? nonProdEnvs[0].link || null : null);
  }, [application]);

  const buildEmbeddingUrl = useCallback(() => {
    if (!application) return null;

    const baseUrl = EmbeddingUtils.getEmbeddingURL(application, selectedEnvLink || undefined);

    if (!baseUrl) {
      return null;
    }

    if (
      productType === EmbeddableProductType.PROPHECY ||
      productType === EmbeddableProductType.SKYNET_TABLEAU ||
      productType === EmbeddableProductType.AMAZON_QUICK
    ) {
      return baseUrl;
    }

    const params = new URLSearchParams({
      embed: 'true',
      source: 'das-fin-suite',
      userAlias,
      isDarkMode: String(initialIsDarkMode)
    });

    return `${baseUrl.replace(/\/+$/, '')}/?${params.toString()}`;
  }, [application, productType, userAlias, initialIsDarkMode, selectedEnvLink]);

  // Initialize embedding URL when context is ready
  useEffect(() => {
    if (!application) return;

    try {
      setReadyToEmbed('in-progress');

      if (contextLoadingStatus !== LoadingStatus.Completed) {
        logger.info('Context not ready yet', { contextLoadingStatus });
        return;
      }

      logger.info('Building embedding URL...');
      const url = buildEmbeddingUrl();

      if (url) {
        logger.info('Embedding URL built successfully', { url });
        setEmbeddingUrl(url);
        setEmbeddingUrlError(null);
        setReadyToEmbed('success');
      } else {
        setEmbeddingUrl(null);
        setEmbeddingUrlError(EmbeddingUtils.EmbeddingURLErrorMessages.URL_NOT_CONFIGURED);
        setReadyToEmbed('error');
      }

      logger.info('Initialized embedding URL');
    } catch (error) {
      logger.error('Error while building URL', { error: error });
      const errorMessage = error instanceof Error ? error.message : EmbeddingUtils.EmbeddingURLErrorMessages.URL_CONFIGURE_UNKNOWN;
      setEmbeddingUrl(null);
      setEmbeddingUrlError(errorMessage);
      setReadyToEmbed('error');
    }
  }, [contextLoadingStatus, buildEmbeddingUrl, application, productType]);

  // Notify parent when application data is missing (via effect to avoid side-effects during render)
  useEffect(() => {
    if (!application) {
      onError?.(new Error('Application data is required'));
    }
  }, [application, onError]);

  // Early validation — safe to return here, after ALL hooks (including useCallback/useEffect)
  if (!application) {
    return <Alert type="error">Application data is missing</Alert>;
  }

  // If either Iframe or Embedding params are not ready, it will display Loading State
  if (readyToEmbed === 'in-progress') {
    return <EmbedAppLoadingState application={application} />;
  }

  // If either embeddingUrlError or iFRame error, it will display Error State
  if (readyToEmbed === 'error') {
    return (
      <EmbedAppErrorState application={application} error={embeddingUrlError || EmbeddingUtils.EmbeddingURLErrorMessages.URL_CONFIGURE_UNKNOWN} />
    );
  }

  // If loading completes without any error, then Render iframe
  // In non-prod with multiple environments, show picker first
  if (showEnvPicker && !selectedEnvLink) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <Cards
          cardDefinition={{
            header: (env) => env.displayText || 'Unnamed',
            sections: [
              {
                id: 'link',
                content: (env) => (
                  <Box color="text-body-secondary" fontSize="body-s">
                    {env.link}
                  </Box>
                )
              }
            ]
          }}
          items={nonProdEnvs}
          header={<Header variant="h2">Select Environment</Header>}
          onSelectionChange={({ detail }) => {
            const env = detail.selectedItems[0];
            if (env?.link) setSelectedEnvLink(env.link);
          }}
          selectionType="single"
          selectedItems={[]}
          entireCardClickable
        />
      </div>
    );
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        window.location.reload();
      }}
    >
      {embeddingUrl ? (
        productType === EmbeddableProductType.SKYNET_TABLEAU ? (
          <TableauVizComponent src={embeddingUrl} title={`Embedded ${application.applicationName}`} />
        ) : productType === EmbeddableProductType.AMAZON_QUICK ? (
          <QuickSightDashboardComponent src={embeddingUrl} title={`Embedded ${application.applicationName}`} />
        ) : (
          <IFrameComponent src={embeddingUrl} title={`Embedded ${application.applicationName}`} />
        )
      ) : (
        <Alert
          action={<FinsuiteIssueActionButton />}
        >{`An error occurred while loading the application. Please contact support for assistance.`}</Alert>
      )}
    </ErrorBoundary>
  );
};
